import { useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import * as recommendations from '@noths/polaris-client-recommendations';
import { getUserAttributes } from '@noths/polaris-client-user-configuration';
import { addRUMTiming } from '@noths/polaris-client-utils';
import getConfig from 'next/config';

import type { Placement } from 'src/constants/recommendations';
import { isProdLike } from 'src/environment';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { actions as richRelevanceActions } from 'src/redux/richRelevance/slice';
import { requestRecommendedProducts } from 'src/redux/richRelevance/thunks';
import { selectUserConfiguration } from 'src/redux/selectors';
import type { CatServiceAPIProduct } from 'src/services/cat-service-api/types/CatServiceAPIProduct';
import type { NextConfig } from 'src/types/nextConfig';

interface UseRecommendedProductsProps {
  categoryId: CatServiceAPIProduct['breadcrumbCategoryId'];
  placements: Placement[];
  productCode: CatServiceAPIProduct['code'];
  productTitle: CatServiceAPIProduct['title'];
}

const { publicRuntimeConfig } = getConfig() as NextConfig;

export const useProductsRecommendations = ({
  categoryId,
  placements,
  productCode,
  productTitle,
}: UseRecommendedProductsProps) => {
  const state = useSelector((state: ReduxApplicationState) => state);
  const userConfiguration = selectUserConfiguration(state);
  const dispatch = useDispatch();

  useEffect(() => {
    getUserAttributes()
      .then((attributes) =>
        recommendations.initRecommendations({
          apiKey: publicRuntimeConfig.RICH_RELEVANCE_API_KEY,
          env: isProdLike() ? 'production' : 'qa',
          sessionId: userConfiguration.richRelevanceSessionId,
          userId: attributes?.sub,
        }),
      )
      .then(async () => {
        recommendations.setProductDetailPage(productCode, productTitle);
        recommendations.setProductContextCategory(String(categoryId!));

        const placementData = await recommendations.getRecommendations(placements);

        const recommendationsRequests = placementData.map((placement) =>
          dispatch(requestRecommendedProducts(placement)),
        );

        const placementsWithoutData = placements.filter((placement) => {
          return !placementData.some((data) => data.placement_name === placement);
        });

        if (placementsWithoutData.length > 0) {
          batch(() => {
            placementsWithoutData.forEach((placementWithoutData) => {
              dispatch(richRelevanceActions.noDataForPlacement(placementWithoutData));
            });
          });
        }

        await Promise.allSettled(recommendationsRequests);
        addRUMTiming('recommendationsLoaded');
      })
      .catch((e) => {
        console.warn(`Could not load recommendations on product details page: ${e.message}`);
      });
  }, []);

  return state.richRelevance;
};
